import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const naira = (v) => `₦${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const StatusPill = ({ status }) => {
  const colors = {
    pending: 'bg-amber-100 text-amber-800', eligible: 'bg-sky-100 text-sky-800', approved: 'bg-emerald-100 text-emerald-800',
    reversed: 'bg-red-100 text-red-700', rejected: 'bg-red-100 text-red-700', successful: 'bg-emerald-100 text-emerald-800',
    pending_approval: 'bg-amber-100 text-amber-800', processing: 'bg-sky-100 text-sky-800', scheduled: 'bg-violet-100 text-violet-800',
    failed: 'bg-red-100 text-red-700',
  };
  const labels = { eligible: 'awaiting approval', pending: 'awaiting delivery', pending_approval: 'awaiting approval' };
  return <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${colors[status] || 'bg-slate-100 text-slate-700'}`} data-testid={`status-${status}`}>{(labels[status] || status || '').replace('_', ' ')}</span>;
};

export const BankForm = ({ token, banks, current, onSaved }) => {
  const [bankCode, setBankCode] = useState(current?.bank_code || '');
  const [account, setAccount] = useState(current?.account_number || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const bank = banks.find((b) => b.code === bankCode);
    if (!bank || !/^\d{10}$/.test(account)) return toast.error('Choose a bank and enter a 10-digit account number');
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/affiliate/me/bank`, { bank_name: bank.name, bank_code: bank.code, account_number: account }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(data.verified ? `Saved: ${data.account_name}` : 'Bank details saved');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save bank details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="bank-form">
      <Select value={bankCode} onValueChange={setBankCode}>
        <SelectTrigger className="h-11 rounded-xl" data-testid="bank-select"><SelectValue placeholder="Select your bank" /></SelectTrigger>
        <SelectContent className="max-h-72">
          {banks.map((b) => <SelectItem key={`${b.code}-${b.name}`} value={b.code}>{b.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Input value={account} onChange={(e) => setAccount(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit account number" className="h-11 rounded-xl" data-testid="bank-account-input" />
      <Button onClick={save} disabled={saving} className="w-full rounded-full bg-health-emerald hover:bg-health-emerald-dark text-white font-bold" data-testid="bank-save-button">
        {saving ? 'Verifying...' : 'Save payout account'}
      </Button>
      <p className="text-xs text-slate-500">For your security, payouts pause for a short time after bank details change.</p>
    </div>
  );
};

export const PayoutRequest = ({ token, available, onDone }) => {
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/affiliate/me/payouts`, { amount: Number(amount) }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(data.status === 'pending_approval' ? 'Payout requested - awaiting approval' : `Payout ${data.status.replace('_', ' ')}`);
      setAmount('');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payout request failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex gap-2" data-testid="payout-request">
      <Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Up to ${naira(available)}`} className="h-11 rounded-xl" data-testid="payout-amount-input" />
      <Button onClick={submit} disabled={busy || !amount} className="rounded-full bg-health-coral hover:bg-health-coral/90 text-white font-bold px-6" data-testid="payout-request-button">
        {busy ? '...' : 'Withdraw'}
      </Button>
    </div>
  );
};
